import type { IAdminTransaction, ITransaction } from "@gen/app-query";

import { type Money, MoneyFactory } from "~/utilities/money";

export function isAdminTransaction(
  tx: ITransaction | IAdminTransaction,
): tx is IAdminTransaction {
  return "client_site" in tx;
}

type Refund = {
  id: string;
  amount: Money;
  refunded: Date;
  accrualNetDelta: Money;
  isPostRelease: boolean;
};

export class TransactionMoney {
  private factory: MoneyFactory;
  private readonly price: Money;
  private readonly priceExtra: Money;
  private readonly pricePostage: Money;
  private readonly postageBearer: string | undefined;
  private readonly charge: Money;
  private readonly chargeBuyerClient: Money;
  private readonly chargeBuyerPostage: Money;
  private readonly remainderPrice: Money;
  private readonly remainderCharge: Money;
  private readonly amountRefunded: Money;
  private readonly amountRefundedPostRelease: Money;
  private readonly clientAccrualNet: Money;
  private readonly refunds: Refund[];
  private readonly buyerPaid: Money;
  private readonly sellerFees: Money;
  private readonly chargeSellerClient: Money;
  private readonly internationalFees: Money;
  private readonly expectedPayout: Money;
  private readonly sellerPayout: Money;
  private readonly total: Money;

  constructor(transaction: ITransaction | IAdminTransaction, locale?: string) {
    try {
      this.factory = new MoneyFactory(transaction.currency, locale);
    } catch (err) {
      throw Error("failed to create money factory:" + err);
    }

    const depositPrice = isAdminTransaction(transaction)
      ? (transaction.deposit_pricing?.price ?? transaction.price ?? 0)
      : (transaction.deposit_pricing?.price ?? transaction.price ?? 0);

    this.price = this.factory.create(depositPrice);

    this.priceExtra = this.factory.create(
      transaction.deposit_pricing?.price_extra ?? 0,
    );

    this.pricePostage = this.factory.create(
      transaction.deposit_pricing?.price_postage ?? 0,
    );

    this.postageBearer = transaction.deposit_pricing?.postage_bearer;

    const depositChargeBuyerClient = isAdminTransaction(transaction)
      ? (transaction.deposit_pricing?.charge_buyer_client ??
        transaction.charge_buyer_client ??
        0)
      : (transaction.deposit_pricing?.charge_buyer_client ??
        transaction.charge_buyer_client ??
        0);

    this.chargeBuyerClient = this.factory.create(depositChargeBuyerClient);

    const depositCharge = isAdminTransaction(transaction)
      ? (transaction.deposit_pricing?.charge ?? transaction.charge ?? 0)
      : (transaction.deposit_pricing?.charge ?? transaction.charge ?? 0);

    this.charge = this.factory
      .create(depositCharge)
      .subtract(this.chargeBuyerClient);

    this.chargeBuyerPostage = this.factory.create(
      transaction.charge_postage_buyer ?? 0,
    );

    // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
    this.remainderPrice = this.factory.zero();
    const totalPrice =
      transaction.deposit_pricing?.price ?? transaction.price ?? 0;

    this.expectedPayout = this.factory.create(totalPrice);

    // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
    if (transaction.pricing?.price) {
      this.remainderPrice = this.factory
        .create(transaction.pricing.price)
        .subtract(this.price);
      this.expectedPayout = this.factory.create(transaction.pricing.price);
    }

    // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
    this.remainderCharge = this.factory.zero();
    if (transaction.pricing?.charge) {
      this.remainderCharge = this.factory
        .create(transaction.pricing.charge)
        .subtract(this.charge);
    }

    this.amountRefunded = this.factory.create(transaction.amount_refunded ?? 0);
    this.clientAccrualNet = this.factory.create(
      transaction.client_accrual_net ?? 0,
    );
    this.refunds = transaction.f2f_refunds
      ? transaction.f2f_refunds.map((refund) => ({
          id: refund.id,
          amount: this.factory.create(refund.amount || 0),
          refunded: refund.refunded,
          accrualNetDelta: this.factory.create(refund.accrual_net_delta ?? 0),
          isPostRelease: refund.is_post_release,
        }))
      : (transaction.online_refunds ?? []).map((refund) => ({
          id: refund.id,
          amount: this.factory.create(refund.amount || 0),
          refunded: refund.refunded,
          accrualNetDelta: this.factory.zero(),
          isPostRelease: false,
        }));

    this.amountRefundedPostRelease = this.refunds
      .filter((refund) => refund.isPostRelease)
      .reduce((total, refund) => total.add(refund.amount), this.factory.zero());

    this.buyerPaid = this.price
      .add(this.priceExtra)
      // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
      .add(this.remainderPrice)
      .add(this.chargeBuyerPostage)
      .add(
        this.postageBearer === "buyer"
          ? this.pricePostage
          : this.factory.zero(),
      )
      .add(this.charge)
      .add(this.chargeBuyerClient)
      // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
      .add(this.remainderCharge)
      .subtract(this.amountRefunded);

    this.chargeSellerClient = this.factory.create(
      transaction.charge_seller_client ?? 0,
    );
    if (transaction.type === "f2f") {
      const depositSellerClientFee = this.factory.create(
        transaction.deposit_pricing?.charge_seller_client ?? 0,
      );
      this.chargeSellerClient = depositSellerClientFee;
    }

    this.sellerFees = this.factory
      .create(transaction.charge_seller ?? 0)
      .subtract(this.chargeSellerClient);
    if (transaction.type === "f2f") {
      const depositSellerFee = this.factory.create(
        transaction.deposit_pricing?.charge_seller ?? 0,
      );
      // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
      const remainderSellerFee = this.factory.create(
        transaction.pricing?.charge_seller ?? 0,
      );
      this.sellerFees = depositSellerFee
        .add(remainderSellerFee)
        .subtract(this.chargeSellerClient);
    }

    this.internationalFees = this.factory.create(
      transaction.charge_international_payment ?? 0,
    );
    if (transaction.type === "f2f") {
      const depositInternationalFee = this.factory.create(
        transaction.deposit_pricing?.charge_international_payment ?? 0,
      );
      const remainderInternationalFee = this.factory.create(
        transaction.pricing?.charge_international_payment ?? 0,
      );
      this.internationalFees = depositInternationalFee.add(
        remainderInternationalFee,
      );
    }

    this.sellerPayout = this.factory.create(transaction.amount_released ?? 0);

    this.expectedPayout = this.expectedPayout
      .subtract(this.sellerFees)
      .subtract(this.chargeSellerClient)
      .subtract(this.internationalFees)
      .subtract(
        this.postageBearer === "seller"
          ? this.pricePostage
          : this.factory.zero(),
      )
      .subtract(this.getAmountRefundedBeforeRelease());

    this.total = this.price
      .add(this.charge)
      .add(this.chargeBuyerClient)
      .add(this.chargeBuyerPostage);
    // NOTE Remainder is deprecated but we need to add it here for backwards compatibility.
    if (!this.remainderPrice.isZero()) {
      this.total = this.remainderPrice.add(this.remainderCharge);
    }
  }

  getPrice(): Money {
    return this.price;
  }

  getPriceExtra(): Money {
    return this.priceExtra;
  }

  getPricePostage(): Money {
    return this.pricePostage;
  }

  getPostageBearer(): string | undefined {
    return this.postageBearer;
  }

  getCharge(): Money {
    return this.charge;
  }

  getChargeBuyerClient(): Money {
    return this.chargeBuyerClient;
  }

  getChargeBuyerPostage(): Money {
    return this.chargeBuyerPostage;
  }

  getRemainderPrice(): Money {
    return this.remainderPrice;
  }

  getRemainderCharge(): Money {
    return this.remainderCharge;
  }

  getAmountRefunded(): Money {
    return this.amountRefunded;
  }

  getAmountRefundedPostRelease(): Money {
    return this.amountRefundedPostRelease;
  }

  getAmountRefundedBeforeRelease(): Money {
    return this.amountRefunded.subtract(this.amountRefundedPostRelease);
  }

  getClientAccrualNet(): Money {
    return this.clientAccrualNet;
  }

  getRefundsAccrualNetDelta(): Money {
    return this.refunds.reduce(
      (total, refund) => total.add(refund.accrualNetDelta),
      this.factory.zero(),
    );
  }

  getBuyerPaid(): Money {
    return this.buyerPaid;
  }

  getSellerFees(): Money {
    return this.sellerFees;
  }

  getChargeSellerClient(): Money {
    return this.chargeSellerClient;
  }

  getInternationalFees(): Money {
    return this.internationalFees;
  }

  getExpectedPayout(
    includeExtraPrice?: boolean,
    includePostagePrice?: boolean,
  ): Money {
    let payout = this.expectedPayout;
    if (includeExtraPrice ?? false) {
      payout = payout.add(this.priceExtra);
    }
    if (includePostagePrice ?? false) {
      payout = payout.add(this.pricePostage);
    }
    return payout;
  }

  getSellerPayout(): Money {
    return this.sellerPayout;
  }

  getTotal(): Money {
    return this.total;
  }

  getRefunds() {
    return this.refunds;
  }

  getPostReleaseRefunds() {
    return this.refunds.filter((refund) => refund.isPostRelease);
  }

  zero(): Money {
    return this.factory.zero();
  }

  create(amount: number): Money {
    return this.factory.create(amount);
  }

  getCurrencyCode(): string {
    return this.factory.getCurrencyCode();
  }

  getCurrencySymbol(): string | undefined {
    return this.factory.getCurrencySymbol();
  }
}
